/**
 * easing — smooth-motion math for the djmsqrvve VFX primitive kit.
 *
 * The "pro look" DJ wants is, mechanically, *everything moves on an eased
 * curve* — soft lights drift on sine arcs, bloom breathes on sine, particles
 * spawn/fade on cubic/quint, nothing translates linearly. This module is the
 * single home for those curves + the path helpers, so every primitive in the
 * kit shares one motion vocabulary.
 *
 * It RE-USES the renderer's proven `Easing` (Tween.ts) rather than redefining
 * sine/cubic — that set is already the substrate-agnostic tween curve table.
 * We only *add* the slow-quint curves (the long, lazy decel that reads as
 * "premium/calm") and the drift/orbit/pulse path helpers, which are pure
 * geometry and have no Tween equivalent.
 *
 * Pure functions only — no Pixi, no ISprite, unit-tested in easing.test.ts.
 */
import { Easing, type EasingFn } from '../../rendering/Tween';
import { clamp01 } from '../../../shared/utils/MathUtils';

/**
 * The kit's curve table. Sine/cubic come straight from the renderer's Tween
 * easings (one source of truth); quint curves are added here for the slow,
 * luxurious decel the soft-light aesthetic leans on.
 */
export const EASING = {
  linear: Easing.linear,
  sineInOut: Easing.sineInOut,
  sineOut: Easing.sineOut,
  cubicOut: Easing.cubicOut,
  /** Quint ease-out: very front-loaded — long, calm settle. */
  quintOut: (t: number) => 1 - Math.pow(1 - t, 5),
  /** Quint ease-in-out: slow both ends, quick middle — premium glide. */
  quintInOut: (t: number) =>
    t < 0.5 ? 16 * t * t * t * t * t : 1 - Math.pow(-2 * t + 2, 5) / 2,
} as const satisfies Record<string, EasingFn>;

export type VfxEasingName = keyof typeof EASING;

export interface Point {
  x: number;
  y: number;
}

/** A bounded drift offset: travel up to `radius` along `angleRad`. */
export interface DriftSpec {
  radius: number;
  angleRad: number;
}

/**
 * Position of a soft light drifting away from `origin` along a fixed bearing,
 * parameterised by phase `t` in [0,1]. Motion is sine-eased so the light
 * eases out to the far point and (with a yoyo'd tween) eases back — the
 * lazy, weightless float of the djmsqrvve lights. Bounded by `radius`.
 */
export function driftPoint(origin: Point, drift: DriftSpec, t: number): Point {
  const k = EASING.sineInOut(clamp01(t));
  return {
    x: origin.x + Math.cos(drift.angleRad) * drift.radius * k,
    y: origin.y + Math.sin(drift.angleRad) * drift.radius * k,
  };
}

/**
 * A point on a circle of `radius` around `center`, at `startAngle` + a full
 * revolution scaled by phase `t` in [0,1]. Used for orbiting accents (sparks
 * circling a bloom). Constant radius by construction.
 */
export function orbitPoint(center: Point, radius: number, startAngle: number, t: number): Point {
  const a = startAngle + clamp01(t) * Math.PI * 2;
  return {
    x: center.x + Math.cos(a) * radius,
    y: center.y + Math.sin(a) * radius,
  };
}

/**
 * Breathing alpha between `min` and `max` on a sine wave of period 1 in `t`.
 * `t=0` sits at `min`; the wave is continuous so phase-shifting `t` by whole
 * periods is a no-op (lets each light breathe out of phase via a t-offset).
 */
export function pulseAlpha(min: number, max: number, t: number): number {
  // (1 - cos(2πt)) / 2 → 0 at t=0, 1 at t=0.5, back to 0 at t=1.
  const wave = (1 - Math.cos(t * Math.PI * 2)) / 2;
  return min + (max - min) * wave;
}
